/**
 * VoiceMemoRecorder Component
 * 
 * Records voice memos for iMessage-style messaging.
 * Features:
 * - Press and hold to record (or click to toggle)
 * - Visual feedback during recording (waveform animation)
 * - Timer showing recording duration
 * - Cancel option (swipe left or click cancel)
 * - Max duration: 10 minutes (auto-stops and sends)
 * - Compressed audio for efficient storage while maintaining voice clarity
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, X, Send, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import UploadProgress from '@/components/shared/UploadProgress';

const MAX_DURATION = 600; // 10 minutes in seconds (R15: matches mobile)

// Generate consistent wave bar heights (deterministic)
const WAVE_HEIGHTS = Array.from({ length: 20 }, (_, i) => {
  const seed = (i * 7 + 3) % 17;
  return 25 + (seed / 17) * 50 + Math.sin(i * 0.8) * 15;
});

export default function VoiceMemoRecorder({ 
  onRecordingComplete, 
  onCancel,
  disabled = false,
  uploadProgress = 0,  // 0 when idle, 1-100 while the parent uploads the recording
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const [autoStopped, setAutoStopped] = useState(false); // Track if we hit the time limit
  
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const timerInterval = useRef(null);
  const streamRef = useRef(null);
  const hasStartedRef = useRef(false); // Prevent double-start
  const recordingStartTime = useRef(null); // Track actual start time for accurate duration
  const autoStopTriggered = useRef(false); // Prevent multiple auto-stop triggers

  // Cleanup function - defined first
  const cleanup = useCallback(() => {
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    hasStartedRef.current = false;
    recordingStartTime.current = null;
    autoStopTriggered.current = false;
  }, []);

  // Revoke audio URL when it changes
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Auto-start recording when component mounts (with guard against double-start)
  useEffect(() => {
    if (!disabled && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop recording function
  const stopRecording = useCallback((isAutoStop = false) => {
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }

    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    
    setIsRecording(false);
    if (isAutoStop) {
      setAutoStopped(true);
    }
  }, []);

  // Auto-stop at max duration and automatically send
  useEffect(() => {
    if (!isRecording) return;
    
    if (duration >= MAX_DURATION && !autoStopTriggered.current) {
      autoStopTriggered.current = true;
      stopRecording(true); // Pass true to indicate auto-stop
    }
  }, [duration, isRecording, stopRecording]);

  // Auto-send when recording is auto-stopped at time limit
  useEffect(() => {
    if (autoStopped && audioBlob && duration > 0) {
      // Automatically send the voice memo
      // Pass the blob directly to parent for upload
      onRecordingComplete({
        blob: audioBlob,
        duration: duration,
        mimeType: audioBlob.type
      });
      // Reset state
      setAudioBlob(null);
      setAudioUrl(null);
      setDuration(0);
      setAutoStopped(false);
    }
  }, [autoStopped, audioBlob, duration, onRecordingComplete]);

  const startRecording = async () => {
    if (disabled) return;
    
    // Prevent multiple starts
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      return;
    }
    
    setError(null);
    setAutoStopped(false);
    autoStopTriggered.current = false;
    
    try {
      // Request microphone permission with voice-optimized settings
      // Lower sample rate (16kHz) is sufficient for voice and reduces file size
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,  // 16kHz is optimal for voice (vs 44.1kHz for music)
          channelCount: 1     // Mono is sufficient for voice
        } 
      });
      
      streamRef.current = stream;

      // Use low bitrate for voice compression while maintaining clarity
      // 24kbps is good for voice (vs 128kbps+ for music)
      // Opus codec is specifically designed for voice and provides excellent compression
      let options = { audioBitsPerSecond: 24000 };
      const formats = [
        'audio/mp4',                   // AAC — plays on iOS, Android, Chrome 112+, Safari, Edge
        'audio/webm;codecs=opus',      // Fallback for Firefox (no mp4 MediaRecorder)
        'audio/webm',
        'audio/ogg;codecs=opus',
        ''
      ];
      
      for (const format of formats) {
        if (!format || MediaRecorder.isTypeSupported(format)) {
          if (format) options.mimeType = format;
          break;
        }
      }

      const recorder = new MediaRecorder(stream, options);
      mediaRecorder.current = recorder;
      audioChunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunks.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        // Create blob from collected chunks
        const blob = new Blob(audioChunks.current, { 
          type: recorder.mimeType || 'audio/mp4' 
        });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        
        // Calculate final duration from actual recording time
        if (recordingStartTime.current) {
          const finalDuration = Math.floor((Date.now() - recordingStartTime.current) / 1000);
          setDuration(finalDuration);
        }
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      };

      // Start recording WITHOUT timeslice - this means ondataavailable 
      // will only fire once when stop() is called, delivering all audio
      // as a single chunk. This prevents duplicate/choppy playback issues.
      recorder.start();
      setIsRecording(true);
      setDuration(0);
      setAudioBlob(null);
      setAudioUrl(null);
      recordingStartTime.current = Date.now();

      // Clear any existing timer
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }

      // Start timer - use time-based calculation for accuracy
      timerInterval.current = setInterval(() => {
        if (recordingStartTime.current) {
          const elapsed = Math.floor((Date.now() - recordingStartTime.current) / 1000);
          setDuration(elapsed);
        }
      }, 1000);

    } catch (err) {
      console.error('Microphone error:', err);
      hasStartedRef.current = false; // Allow retry
      
      let message = 'Could not access microphone.';
      if (err.name === 'NotAllowedError') {
        message = 'Microphone permission denied. Please allow microphone access in your browser settings.';
      } else if (err.name === 'NotFoundError') {
        message = 'No microphone found. Please connect a microphone.';
      }
      setError(message);
    }
  };

  const cancelRecording = () => {
    cleanup();
    setIsRecording(false);
    setDuration(0);
    setAudioBlob(null);
    setAudioUrl(null);
    setAutoStopped(false);
    if (onCancel) onCancel();
  };

  const sendVoiceMemo = () => {
    if (audioBlob && duration > 0) {
      // Pass the blob directly to parent for upload.
      // Parent (MessageComposer) handles the upload to Supabase and dismisses
      // the recorder when done. We DELIBERATELY do NOT clear audioBlob/duration
      // here — clearing them would trigger this component to fall through to
      // the `return null` branch, hiding the preview UI mid-upload and leaving
      // the user with no visible indicator. The component is dismissed by the
      // parent unmounting it after the upload completes.
      onRecordingComplete({
        blob: audioBlob,
        duration: duration,
        mimeType: audioBlob.type
      });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate time remaining for display when approaching limit
  const timeRemaining = MAX_DURATION - duration;
  const showTimeWarning = isRecording && timeRemaining <= 30; // Show warning in last 30 seconds

  // If we have a recorded audio, show preview mode.
  // During upload (uploadProgress > 0), keep showing the preview but disable
  // the Cancel button — once Send is clicked, the upload is committed.
  // Disabling Cancel matches Slack/WhatsApp's "no cancel after send" UX and
  // prevents the race where the user dismisses the recorder while XHR is
  // still in flight (the message would land anyway).
  if (audioBlob && audioUrl && !autoStopped) {
    const isUploading = uploadProgress > 0;
    return (
      <div data-ui="rec-pill" data-mode="prev" className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-[#3a3d41] rounded-2xl" data-testid="voice-memo-preview">
        {/* Cancel button — disabled during upload so the user can't dismiss
            the recorder mid-XHR and still have the message land. */}
        <Button
          variant="ghost"
          size="icon"
          onClick={cancelRecording}
          disabled={isUploading}
          className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-red-500 disabled:opacity-40 flex-shrink-0"
          data-testid="voice-memo-cancel"
        >
          <Trash2 className="w-4 h-4" />
        </Button>

        {/* Audio preview info */}
        <div className="flex-1 flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Mic className="w-4 h-4 text-blue-500 dark:text-blue-400" />
            <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">
              Voice memo
            </span>
          </div>
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {formatTime(duration)}
          </span>
        </div>

        {/* Send button — shows live upload % while parent is uploading */}
        <Button
          onClick={sendVoiceMemo}
          disabled={isUploading}
          data-ui="rec-stop"
          className={`bg-blue-500 hover:bg-blue-600 text-white rounded-full h-8 w-8 p-0 flex-shrink-0 flex items-center justify-center`}
          title={!isUploading ? 'Send' : uploadProgress >= 90 && uploadProgress < 100 ? 'Processing voice memo…' : `Uploading ${uploadProgress}%`}
          data-testid="voice-memo-send"
        >
          {isUploading ? (
            <UploadProgress progress={uploadProgress} size={16} className="text-white" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </Button>
      </div>
    );
  }

  // Recording mode
  if (isRecording) {
    return (
      <div data-ui="rec-pill" data-mode="rec" className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-950/40 rounded-2xl border border-red-200 dark:border-red-900" data-testid="voice-memo-recording">
        {/* Cancel button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={cancelRecording}
          className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-red-500 flex-shrink-0"
          data-testid="voice-memo-cancel-recording"
        >
          <X className="w-4 h-4" />
        </Button>

        {/* Waveform animation */}
        <div className="flex-1 flex items-center gap-1">
          <div data-ui="rec-bars" className="flex items-center gap-0.5 h-6">
            {WAVE_HEIGHTS.map((height, i) => (
              <div
                key={i}
                className="w-0.5 bg-red-400 rounded-full animate-pulse"
                style={{
                  height: `${height}%`,
                  minHeight: '4px',
                  animationDelay: `${i * 50}ms`,
                  animationDuration: '300ms'
                }}
              />
            ))}
          </div>
          <div className="ml-2 flex items-center gap-1">
            <span className={`text-sm font-mono ${showTimeWarning ? 'text-orange-600 dark:text-orange-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatTime(duration)}
            </span>
            {showTimeWarning && (
              <span className="text-xs text-orange-500">
                ({formatTime(timeRemaining)} left)
              </span>
            )}
          </div>
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse ml-1" />
        </div>

        {/* Stop/Send button */}
        <Button
          onClick={() => stopRecording(false)}
          data-ui="rec-stop"
          data-tone="bad"
          className="bg-red-500 hover:bg-red-600 text-white rounded-full h-8 w-8 p-0 flex-shrink-0"
          data-testid="voice-memo-stop"
        >
          <MicOff className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div data-ui="rec-err" className="flex items-center gap-2 px-3 py-2 text-red-500 dark:text-red-400 text-sm">
        <X className="w-4 h-4" />
        <span>{error}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setError(null)}
          className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200"
        >
          Dismiss
        </Button>
      </div>
    );
  }

  // Default - mic button only (shown from MessageComposer)
  return null;
}

// Export the start recording trigger for external use
export function VoiceMemoButton({ onClick, disabled }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      data-ui="mic-btn"
      className="text-slate-400 dark:text-slate-500 hover:text-blue-500 flex-shrink-0 h-10 w-10"
      disabled={disabled}
      onClick={onClick}
      title="Record voice memo"
      data-testid="voice-memo-button"
    >
      <Mic className="w-5 h-5" />
    </Button>
  );
}
